import { useCallback, useRef, useEffect, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import ReplySubmitButton from './ReplySubmitButton';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import styled from 'foundations-theming/utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { trackEvent } from '../../../usage-tracking/clients/usageTracker';
// TODO: move to composer-ui
import { getReplyAndCloseClicked } from 'conversations-thread-view/tickets/constants/Events';
// TODO: move to composer-ui
import { messageSent } from 'conversations-thread-view/contextual-prompts/actions/messageSent';
import { THREAD_SWITCHING_MARKER, SEND_BUTTON_READY, SEND_MESSAGE
// TODO: move to composer-ui
} from 'conversations-thread-view/performance-metrics/constants/performanceConstants';
import { getIsUngatedForLongTaskReporting } from 'conversations-thread-data/auth/public/selectors';
// TODO: move to composer-ui
import { useThreadDetails } from 'conversations-thread-view/thread-details-feature/public/hooks/useThreadDetails';
// TODO: move to composer-ui
import { isBotConversationWithRecentMessages } from 'conversations-thread-view/reassignment/operators/isBotConversationWithRecentMessages';
// TODO: move to composer-ui
import { clearPerformanceMarksAndMeasures } from 'conversations-thread-view/performance-metrics/operators/gatherPerformanceMetrics';
import { CHANNEL_TYPES } from 'conversations-thread-data/channel-configuration/public/constants';
// @ts-expect-error module not typed - TODO: move to composer-ui
import { usesTaxonomyFlow } from 'conversations-thread-view/selectors/conversationSelectors/onCloseSelectors/usesTaxonomyFlow';
import { Metrics } from '../../../metrics/protected/Metrics';
import { getUsageTrackingChannel } from 'conversations-thread-data/usage-tracking/public/trackerChannelIdMapping';
import Raven from 'raven-js';
import { reportError } from 'conversations-error-reporting/error-reporting/reportError';
import { MultiLevelDropdown } from 'foundations-components/transitional/components/Dropdown';
import { Button } from 'foundations-components/transitional/components/Button';
import { DownCaratIcon } from 'foundations-assets';
import { SEND_BUTTON_ENABLED_FIRST_LOAD, SEND_BUTTON_ENABLED_THREAD_SWITCH } from '../../../metrics/protected/constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MultiLevelDropdownButton = /*#__PURE__*/memo(props => {
  return /*#__PURE__*/_jsx(Button, Object.assign({
    "data-test-id": "reply-submit-more-options"
  }, props, {
    size: "small",
    use: "primary",
    children: /*#__PURE__*/_jsx(DownCaratIcon, {
      size: "200"
    })
  }));
});
MultiLevelDropdownButton.displayName = 'MultiLevelDropdownButton';
export const StyledFlex = styled.div.withConfig({
  displayName: "ReplySubmitButtonGroup__StyledFlex",
  componentId: "sc-17c9sll-0"
})(["display:flex;.empty-div-spacer-enter{width:0 !important;}"]);
const usePerformanceLogging = ({
  disabledAwaitingUserAction,
  genericChannelId,
  threadId
}) => {
  const hasPerformanceLoggedRef = useRef(false);
  const shouldReport = useSelector(getIsUngatedForLongTaskReporting);
  useEffect(() => {
    try {
      if (hasPerformanceLoggedRef.current || !threadId || !disabledAwaitingUserAction) {
        return;
      }
      const startMarker = `${THREAD_SWITCHING_MARKER}-start-${threadId}`;
      const startEntries = performance.getEntriesByName(`${THREAD_SWITCHING_MARKER}-start-${threadId}`);
      if (startEntries && startEntries.length > 0) {
        hasPerformanceLoggedRef.current = true;
        const stopMarker = `conversations-reply-enabled-${threadId}`;
        const durationMarker = `conversations-reply-enabled-wait-time-${threadId}`;
        performance.mark(stopMarker);
        performance.measure(durationMarker, startMarker, stopMarker);
        const duration = performance.getEntriesByName(durationMarker)[0].duration;
        if (shouldReport) {
          const data = {
            channel: getUsageTrackingChannel(genericChannelId),
            duration
          };
          Raven.capturePageEvent('replyButtonEnabled', {
            extra: data
          });
        }
        Metrics.timer(SEND_BUTTON_ENABLED_THREAD_SWITCH, {
          genericChannelId: `${genericChannelId}`
        }).update(Math.round(duration));
      }
    } catch (e) {
      // quiet catch, performance API is iffy per browser
      // eslint-disable-next-line no-console
      console.error('Submit Button Performance Error', e);
    }
  }, [disabledAwaitingUserAction, genericChannelId, threadId, shouldReport]);
};
//Logs the duration between page load and when the send button reaches a state
//where it is able to become enabled.
const useInitialPerformanceLogging = ({
  genericChannelId,
  disabledAwaitingUserAction
}) => {
  const hasBeenLogged = performance.getEntriesByName(SEND_BUTTON_READY).length > 0;
  useEffect(() => {
    if (!hasBeenLogged && disabledAwaitingUserAction) {
      const navigationEntry = performance.getEntriesByType('navigation')[0];
      const pageReceivedMarker = navigationEntry && navigationEntry.toJSON().responseEnd;
      performance.mark(SEND_BUTTON_READY);
      const buttonEnabledMarker = performance.getEntriesByName(SEND_BUTTON_READY)[0].startTime;
      const duration = buttonEnabledMarker - pageReceivedMarker;
      if (pageReceivedMarker) {
        Raven.setExtraContext({
          [SEND_BUTTON_READY]: duration
        });
      }
      Metrics.timer(SEND_BUTTON_ENABLED_FIRST_LOAD, {
        genericChannelId: `${genericChannelId}`
      }).update(Math.round(duration));
    }
  }, [disabledAwaitingUserAction, genericChannelId, hasBeenLogged]);
};
export const ReplySubmitButtonGroup = props => {
  const {
    onClick,
    disabled,
    disabledAwaitingUserAction = false,
    associatedTicket,
    associatedTicketError,
    associatedTicketIsLoading,
    genericChannelId,
    threadId,
    channelType,
    recipientCount,
    openTaxonomyPanel,
    sendOptions,
    renderSendOptionsSeatUGP,
    onBeforeDefaultSend,
    compact
  } = props;
  usePerformanceLogging({
    disabledAwaitingUserAction,
    genericChannelId,
    threadId
  });
  useInitialPerformanceLogging({
    disabledAwaitingUserAction,
    genericChannelId
  });
  const {
    asyncThreadDetails
  } = useThreadDetails({
    threadId
  });
  const isActiveBotConversation = isBotConversationWithRecentMessages(getData(asyncThreadDetails));
  const dispatch = useDispatch();
  const taxonomyFlow = useSelector(state => usesTaxonomyFlow(state, props.inboxId));
  const onClickWithMessageSentAction = useCallback((...args) => {
    clearPerformanceMarksAndMeasures(SEND_MESSAGE);
    performance.mark(`${SEND_MESSAGE}-start`, {
      startTime: Date.now()
    });
    dispatch(messageSent());
    onClick(...args);
  }, [dispatch, onClick]);
  const onReplyAndCloseClicked = useCallback(() => {
    const ticketsCount = associatedTicket ? 1 : 0;
    trackEvent(getReplyAndCloseClicked({
      ticketAssociated: Boolean(ticketsCount)
    }));
    const stubEvent = new Event('ReplyAndClose');
    // @ts-expect-error this is probably meant to be stubbed even if the types aren't perfect
    onClickWithMessageSentAction(stubEvent, {
      closeOnSuccess: true
    });
    if (taxonomyFlow && openTaxonomyPanel) {
      openTaxonomyPanel();
    }
  }, [associatedTicket, onClickWithMessageSentAction, openTaxonomyPanel, taxonomyFlow]);
  const disabledReplyAndCloseButton = disabled || associatedTicketIsLoading || associatedTicketError || isActiveBotConversation;
  const replyAndCloseOptions = useMemo(() => [{
    label: recipientCount === 1 ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "composer-ui.send-button.reply-submit-button-group.submit-and-close"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "composer-ui.send-button.reply-submit-button-group.submit-and-close-count",
      options: {
        count: recipientCount
      }
    }),
    onClick: onReplyAndCloseClicked,
    disabled: disabledReplyAndCloseButton,
    id: 'reply-and-close'
  }], [disabledReplyAndCloseButton, onReplyAndCloseClicked, recipientCount]);
  const customSendOptions = (sendOptions || []).map(sendOption => Object.assign({}, sendOption, {
    disabled: sendOption.disabled || disabledReplyAndCloseButton,
    onClick: () => {
      const stubEvent = new Event('SendOption');
      // @ts-expect-error this is probably meant to be stubbed even if the types aren't perfect
      onClickWithMessageSentAction(stubEvent, {
        closeOnSuccess: false
      });
      sendOption.onClick();
    }
  }));

  // Add UGP to the top of menu if missing seat permission
  const seatUGPLabel = renderSendOptionsSeatUGP === null || renderSendOptionsSeatUGP === void 0 ? void 0 : renderSendOptionsSeatUGP();
  if (seatUGPLabel) {
    customSendOptions.unshift({
      label: seatUGPLabel,
      disabled: false,
      onClick: () => {}
    });
  }
  const defaultSendOnClick = useCallback(async (...args) => {
    if (onBeforeDefaultSend) {
      try {
        await onBeforeDefaultSend({
          genericChannelId
        });
      } catch (ex) {
        reportError({
          error: ex
        });
      }
    }
    onClickWithMessageSentAction(...args);
  }, [onClickWithMessageSentAction, onBeforeDefaultSend, genericChannelId]);

  // Only render the send button dropdown when there is no ticket in inbox associated to the thread
  const hasNoTicket = !associatedTicketIsLoading && !associatedTicketError && !associatedTicket;
  const showCustomSendOptions = customSendOptions.length > 0;
  const shouldShowSendButtonDropdown = channelType !== CHANNEL_TYPES.INTERNAL && (hasNoTicket || showCustomSendOptions);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(StyledFlex, {
      children: /*#__PURE__*/_jsxs(ButtonGroup, {
        children: [/*#__PURE__*/_jsx(ReplySubmitButton, Object.assign({}, props, {
          onClick: defaultSendOnClick,
          compact: compact
        })), shouldShowSendButtonDropdown && /*#__PURE__*/_jsx(MultiLevelDropdown, {
          buttonSize: "small",
          Button: MultiLevelDropdownButton,
          disabled: disabled,
          placement: "top left",
          options: showCustomSendOptions ? customSendOptions : replyAndCloseOptions
        })]
      })
    })
  });
};
ReplySubmitButtonGroup.displayName = 'ReplySubmitButtonGroup';
ReplySubmitButtonGroup.propTypes = {
  associatedTicket: PropTypes.shape({
    id: PropTypes.number,
    defaultProperties: PropTypes.object,
    properties: PropTypes.array,
    pipelineInfo: PropTypes.object
  }),
  associatedTicketError: PropTypes.object,
  associatedTicketIsLoading: PropTypes.bool,
  channelType: PropTypes.oneOf(Object.keys(CHANNEL_TYPES)).isRequired,
  disabled: PropTypes.bool,
  inboxId: PropTypes.number,
  onClick: PropTypes.func.isRequired,
  openTaxonomyPanel: PropTypes.func
};
export default ReplySubmitButtonGroup;